import { hasPermission } from './permissions/usePermissions';
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Menu, Button } from 'antd';
import {
  MenuFoldOutlined, MenuUnfoldOutlined, HomeOutlined, TeamOutlined,
  ShopOutlined, AppstoreOutlined, UserOutlined, ShoppingOutlined,
  BranchesOutlined, FileDoneOutlined, FileTextOutlined, RollbackOutlined,
  UploadOutlined, NumberOutlined, FileExcelOutlined, SettingOutlined,
  DatabaseOutlined, TruckOutlined, BugOutlined, ProfileOutlined,
  DownloadOutlined,
} from '@ant-design/icons';

function visibleItems(items) {
  return items.filter(item => item.permission).map(item => item.children
    ? { ...item, children: visibleItems(item.children) } : item)
    .filter(item => !item.children || item.children.length > 0);
}

function selectedPath(items, location) {
  const currentQuery = new URLSearchParams(location.search);
  for (const item of items) {
    if (item.to) {
      const [pathname, search = ''] = item.to.split('?');
      if (pathname === location.pathname && [...new URLSearchParams(search)].every(([key, value]) => currentQuery.get(key) === value)) {
        return [item.key];
      }
    }
    if (item.children) {
      const childPath = selectedPath(item.children, location);
      if (childPath.length) return [item.key, ...childPath];
    }
  }
  return [];
}

const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [openKeys, setOpenKeys] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector(state => state.user);
  const can = permission => hasPermission(user, permission);
  const manager = can('IsManager');
  const link = (key, label, icon, to, permission = true) => ({ key, label, icon, to, permission });

  const menuItems = visibleItems([
    link('home', 'Home', <HomeOutlined />, '/'),
    link('profile', 'Mon Profil', <ProfileOutlined />, '/profile'),
    {
      key: 'settings', label: 'Settings', icon: <SettingOutlined />, permission: true,
      children: [
        {
          key: 'societe', label: 'Société', icon: <ShopOutlined />, permission: true,
          children: [
            { key: 'societe-info', label: 'Info société (à venir)', icon: <ProfileOutlined />, disabled: true, permission: manager },
            link('bulk-counter', 'Compteurs', <NumberOutlined />, '/bulk?type=counter', can('counters')),
          ],
        },
        link('user', 'User Management', <UserOutlined />, '/user', manager),
        link('template', 'Impression', <BranchesOutlined />, '/drag', can('templates')),
      ],
    },
    {
      key: 'bases', label: 'Bases', icon: <DatabaseOutlined />, permission: true,
      children: [
        link('lists', 'Listes', <DatabaseOutlined />, '/lists', ['deliveryModes', 'paymentModes', 'countries', 'cities', 'banks'].some(can)),
        link('entrepot', 'Entrepôts', <DatabaseOutlined />, '/entrepot', can('warehouses')),
        link('four', 'Fournisseurs', <ShopOutlined />, '/four', can('suppliers')),
        link('article-list', 'Articles', <ShoppingOutlined />, '/article', can('articles')),
        link('client', 'Clients', <TeamOutlined />, '/client', can('clients')),
        link('com', 'Commerciaux', <UserOutlined />, '/com', can('commercials')),
      ],
    },
    {
      key: 'operations-internes', label: 'Opérations internes', icon: <AppstoreOutlined />, permission: true,
      children: [
        link('correctionstock', 'Correction stock (E/S)', <FileTextOutlined />, '/avoir?view=correctionstock', can('stockCorrections')),
        link('demarque', 'Démarque', <FileTextOutlined />, '/avoir?view=demarque', can('demarque')),
        link('transfert', 'Transfert interne', <TruckOutlined />, '/transfert', can('transfers')),
        link('inventaire', 'Inventaire', <DatabaseOutlined />, '/inventaire', can('inventory')),
      ],
    },
    {
      key: 'historiques', label: 'Historiques', icon: <ProfileOutlined />, permission: true,
      children: [
        link('audit', 'Journal d’activité', <ProfileOutlined />, '/audit', can('audit')),
        link('audit-logins', 'Journal de connexion', <UserOutlined />, '/audit/logins', can('audit')),
        link('bulk-debugging', 'Debugging', <BugOutlined />, '/bulk?type=debugging', can('tree')),
      ],
    },
    {
      key: 'echanges-internes', label: 'Échanges internes', icon: <DatabaseOutlined />, permission: true,
      children: [
        link('bulk-importdevis', 'Import Devis', <FileTextOutlined />, '/bulk?type=importdevis'),
        link('bulk-exportdbf', 'Export réponse', <FileExcelOutlined />, '/bulk?type=exportdbf'),
        link('bulk-exportexpedition', 'Export Expedition', <TruckOutlined />, '/bulk?type=exportexpedition'),
        link('bulk-downloadtables', 'Télécharger Tables', <DownloadOutlined />, '/bulk?type=downloadtables'),
        link('bulk-downloadachattables', 'Télécharger Tables Achat', <DownloadOutlined />, '/bulk?type=downloadachattables'),
      ],
    },
    {
      key: 'bulk', label: 'Import lourds', icon: <UploadOutlined />, permission: true,
      children: [
        link('bulk-commercials', 'Upload Commercials', <TeamOutlined />, '/bulk?type=commercials', manager),
        link('bulk-families', 'Upload Families', <BranchesOutlined />, '/bulk?type=families', manager),
        link('bulk-subfamilies', 'Upload Subfamilies', <BranchesOutlined />, '/bulk?type=subfamilies', manager),
        link('bulk-fournisseurs', 'Upload Fournisseurs', <ShopOutlined />, '/bulk?type=fournisseurs', manager),
        link('bulk-articles', 'Upload Articles', <ShoppingOutlined />, '/bulk?type=articles', manager),
        link('bulk-clients', 'Upload Clients', <UserOutlined />, '/bulk?type=clients', manager),
        link('bulk-avoirs', 'Upload Avoirs (DBF)', <RollbackOutlined />, '/bulk?type=avoirs', manager),
        link('bulk-factures', 'Upload Facture (DBF)', <FileTextOutlined />, '/bulk?type=factures', manager),
        link('bulk-bonliv', 'Upload Bonliv (DBF)', <FileDoneOutlined />, '/bulk?type=bonliv', manager),
        link('bulk-achats', 'Upload BonReception (DBF)', <FileTextOutlined />, '/bulk?type=achats', manager),
        link('bulk-demarque', 'Upload Demarque (DBF)', <FileTextOutlined />, '/bulk?type=demarque', manager),
        link('bulk-archive', 'Archive Records', <DatabaseOutlined />, '/bulk?type=archive', can('archives')),
      ],
    },
  ]);

  const activePath = selectedPath(menuItems, location);
  const activePathKey = activePath.join('/');
  useEffect(() => {
    if (!collapsed) {
      const ancestors = activePathKey.split('/').slice(0, -1);
      setOpenKeys(previous => [...new Set([...previous, ...ancestors])]);
    }
  }, [activePathKey, collapsed]);

  const toMenuItems = items => items.map(({ permission, to, children, ...item }) => ({
    ...item,
    ...(to ? { onClick: () => navigate(to) } : {}),
    ...(children ? { children: toMenuItems(children) } : {}),
    ...(item.key === 'societe' ? {
      onTitleMouseEnter: () => setOpenKeys(previous => [...new Set([...previous, 'settings', 'societe'])]),
    } : {}),
  }));

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('sidebarCollapsed', JSON.stringify(next));
  };

  return (
    <div style={{ height: '100%', backgroundColor: '#fff', borderRight: '1px solid #f0f0f0' }}>
      <Button type="text" aria-label={collapsed ? 'Développer le menu' : 'Réduire le menu'}
        onClick={toggleCollapsed} style={{ margin: '16px', marginBottom: '24px' }}>
        {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
      </Button>
      <Menu items={toMenuItems(menuItems)} selectedKeys={activePath.slice(-1)} openKeys={openKeys}
        onOpenChange={setOpenKeys} mode="inline" theme="light" inlineCollapsed={collapsed}
        triggerSubMenuAction="hover" />
    </div>
  );
};

export default Sidebar;
